import { useEffect, useRef, useState, useCallback } from "react";
import {
  FlaskConical, Rocket, Loader2, Mic, PhoneOff, PhoneCall, Send, Bot, User,
  Save, Plus, Trash2, CheckCircle2, Phone, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, VAPI_PUBLIC_KEY } from "@/lib/api";
import { toast } from "sonner";

const ALL_LANGS = ["English", "Spanish", "French", "Arabic", "German", "Polish", "Italian", "Portuguese"];
const VOICES = ["Multilingual (Auto)", "Emma (Female, British)", "James (Male, British)", "Olivia (Female, US)", "Noah (Male, US)"];

let _seq = 0;
const mk = (role, text) => ({ id: `m${++_seq}`, role, text });
const newSession = () => window.crypto?.randomUUID?.() || `sess-${Date.now()}`;

export default function MyReceptionist() {
  const [cfg, setCfg] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [servicesText, setServicesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  // chat
  const [session] = useState(newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // voice
  const vapiRef = useRef(null);
  const [callState, setCallState] = useState("idle");
  const [speaking, setSpeaking] = useState(false);
  const [voiceErr, setVoiceErr] = useState("");

  const load = useCallback(async () => {
    const [b, a] = await Promise.all([api.get("/business"), api.get("/business/assistant")]);
    setCfg(b.data);
    setServicesText((b.data.services || []).join(", "));
    setAssistant(a.data);
    setMessages([mk("assistant", b.data.greeting || "Hello! How can I help you today?")]);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const toggleLang = (l) => set("languages", (cfg.languages || []).includes(l) ? cfg.languages.filter((x) => x !== l) : [...(cfg.languages || []), l]);
  const setFaq = (i, k, v) => { const f = [...cfg.faqs]; f[i] = { ...f[i], [k]: v }; set("faqs", f); };
  const addFaq = () => set("faqs", [...(cfg.faqs || []), { question: "", answer: "", _k: `faq${Date.now()}` }]);
  const removeFaq = (i) => set("faqs", cfg.faqs.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, services: servicesText.split(",").map((s) => s.trim()).filter(Boolean) };
      const r = await api.put("/business", payload);
      setCfg(r.data);
      setServicesText((r.data.services || []).join(", "));
      toast.success("Saved — your AI updated instantly. Test the new behaviour now.");
    } catch (e) {
      toast.error("Could not save changes");
    } finally { setSaving(false); }
  };

  const activate = async () => {
    setActivating(true);
    try {
      await api.post("/subscription/activate");
      await load();
      toast.success("🎉 Congratulations! Your AI Receptionist is now live and ready to answer calls.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate. Please try again.");
    } finally { setActivating(false); }
  };

  const sendMessage = async (text) => {
    const t = (text || "").trim();
    if (!t || busy) return;
    setInput("");
    setMessages((m) => [...m, mk("user", t)]);
    setBusy(true);
    try {
      const r = await api.post("/chat", { session_id: session, message: t });
      setMessages((m) => [...m, mk("assistant", r.data.reply)]);
    } catch (e) {
      setMessages((m) => [...m, mk("assistant", "Sorry, I had trouble connecting. Please try again.")]);
    } finally { setBusy(false); }
  };

  const startVoice = async () => {
    if (!assistant?.vapi_assistant_id || !VAPI_PUBLIC_KEY) {
      setVoiceErr("Voice testing needs your AI to be created. Please save your setup first.");
      return;
    }
    try {
      setVoiceErr("");
      setCallState("connecting");
      const { default: Vapi } = await import("@vapi-ai/web");
      const vapi = new Vapi(VAPI_PUBLIC_KEY);
      vapiRef.current = vapi;
      vapi.on("call-start", () => setCallState("active"));
      vapi.on("call-end", () => { setCallState("idle"); setSpeaking(false); });
      vapi.on("speech-start", () => setSpeaking(true));
      vapi.on("speech-end", () => setSpeaking(false));
      vapi.on("error", (e) => { setCallState("idle"); setSpeaking(false); setVoiceErr(e?.errorMsg || "Couldn't start the call. Check your Vapi credits."); });
      vapi.on("message", (msg) => {
        if (msg.type === "transcript" && msg.transcriptType === "final") {
          setMessages((m) => [...m, mk(msg.role === "assistant" ? "assistant" : "user", msg.transcript)]);
        }
      });
      vapi.start(assistant.vapi_assistant_id);
    } catch (e) {
      setCallState("idle");
      setVoiceErr("Couldn't connect to the voice service. Please try again.");
    }
  };
  const stopVoice = () => { vapiRef.current?.stop(); setCallState("idle"); };

  if (!cfg) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading your AI receptionist…</div>;

  const isLive = assistant?.is_live;
  const phone = assistant?.phone_setup || {};

  return (
    <div data-testid="my-receptionist-view">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">My AI Receptionist</h1>
      <p className="text-slate-500 mt-1">Test and refine your AI before it answers real calls — make changes and hear them instantly.</p>

      {/* Status banner */}
      {isLive ? (
        <div data-testid="live-banner" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white shrink-0"><CheckCircle2 className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-emerald-800">🟢 LIVE — your AI Receptionist is answering calls</p>
            <p className="text-sm text-emerald-700/80 mt-0.5">You can still edit and test changes here anytime — updates apply to your live assistant instantly.</p>
          </div>
        </div>
      ) : (
        <div data-testid="test-mode-banner" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center text-white shrink-0"><FlaskConical className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-amber-800">🧪 TEST MODE</p>
            <p className="text-sm text-amber-700/90 mt-0.5">
              Your AI Receptionist has been created successfully — configured specifically for your business.
              Test conversations below before making it live. <b>No real customer calls reach this assistant while Test Mode is active.</b>
            </p>
          </div>
        </div>
      )}

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        {/* ---- Test panel ---- */}
        <div className="space-y-5">
          {/* Voice */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight">Test by voice</h2>
              <span className="text-xs font-semibold text-slate-400">Powered by your assistant</span>
            </div>
            <div className="mt-5 flex flex-col items-center gap-4 py-2">
              <div className={`relative h-24 w-24 rounded-full flex items-center justify-center ${callState === "active" ? "voice-ripple" : ""}`}>
                <div className={`h-20 w-20 rounded-full flex items-center justify-center transition-colors ${callState === "active" ? "bg-blue-600" : "bg-slate-900"}`}>
                  {callState === "connecting" ? <Loader2 className="h-8 w-8 text-white animate-spin" /> : <Mic className={`h-8 w-8 text-white ${speaking ? "scale-110" : ""} transition-transform`} />}
                </div>
              </div>
              <p data-testid="voice-status" className="text-sm font-semibold text-slate-700">
                {callState === "idle" ? "Tap to start a live voice test" : callState === "connecting" ? "Connecting…" : speaking ? "Speaking…" : "Listening…"}
              </p>
              {voiceErr && <p data-testid="voice-error" className="text-xs text-red-600 text-center max-w-[260px]">{voiceErr}</p>}
              {callState === "idle" ? (
                <Button data-testid="start-voice-test" onClick={startVoice} className="rounded-full bg-blue-600 hover:bg-blue-700 gap-2 px-6"><PhoneCall className="h-4 w-4" /> Start voice test</Button>
              ) : (
                <Button data-testid="stop-voice-test" onClick={stopVoice} variant="destructive" className="rounded-full gap-2 px-6"><PhoneOff className="h-4 w-4" /> End test</Button>
              )}
            </div>
          </div>

          {/* Text chat */}
          <div className="rounded-xl border border-slate-200 bg-white flex flex-col" style={{ height: 380 }}>
            <div className="px-5 py-3 border-b border-slate-100"><h2 className="font-heading font-bold tracking-tight text-sm">Test by text</h2></div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.map((m) => (
                <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && <div className="h-7 w-7 shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white"><Bot className="h-4 w-4" /></div>}
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-slate-900 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>{m.text}</div>
                  {m.role === "user" && <div className="h-7 w-7 shrink-0 rounded-full bg-slate-200 flex items-center justify-center text-slate-600"><User className="h-4 w-4" /></div>}
                </div>
              ))}
              {busy && <div className="text-xs text-slate-400 pl-9">typing…</div>}
            </div>
            <div className="p-3 border-t border-slate-100 flex items-center gap-2">
              <Input data-testid="test-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage(input)} placeholder="Ask your receptionist anything…" className="rounded-full" />
              <Button data-testid="test-chat-send" onClick={() => sendMessage(input)} disabled={busy} size="icon" className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
            </div>
          </div>

          {/* Phone number */}
          <div data-testid="phone-section" className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-3"><Phone className="h-4 w-4 text-slate-400" /><h2 className="font-heading font-bold tracking-tight">Phone number</h2></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Business number</span><span className="font-semibold">{phone.business_number || cfg.phone || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">AI number</span><span className="font-semibold">{phone.ai_number || (phone.option === "operamind" ? "Assigned on activation" : "—")}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`font-semibold ${isLive ? "text-emerald-600" : "text-amber-600"}`}>{isLive ? "Active" : "Inactive (test mode)"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Call forwarding</span><span className="font-semibold">{cfg.forwarding_number || "Not set"}</span></div>
            </div>
          </div>
        </div>

        {/* ---- Edit panel ---- */}
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight">Edit your AI</h2>
              <Button data-testid="save-receptionist-btn" onClick={save} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & test</Button>
            </div>
            <div><Label className="text-xs">Greeting</Label><Textarea data-testid="edit-greeting" value={cfg.greeting || ""} onChange={(e) => set("greeting", e.target.value)} rows={2} className="mt-1" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Opening hours</Label><Input data-testid="edit-hours" value={cfg.hours || ""} onChange={(e) => set("hours", e.target.value)} className="mt-1" /></div>
              <div>
                <Label className="text-xs">Voice</Label>
                <Select value={cfg.voice || "Multilingual (Auto)"} onValueChange={(v) => set("voice", v)}>
                  <SelectTrigger data-testid="edit-voice" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Services (comma separated)</Label><Input data-testid="edit-services" value={servicesText} onChange={(e) => setServicesText(e.target.value)} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Languages</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ALL_LANGS.map((l) => {
                  const on = (cfg.languages || []).includes(l);
                  return <button key={l} type="button" data-testid={`edit-lang-${l}`} onClick={() => toggleLang(l)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{l}</button>;
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs">FAQs</Label><Button data-testid="add-faq-btn" variant="ghost" size="sm" onClick={addFaq} className="h-7 gap-1 text-blue-600"><Plus className="h-3.5 w-3.5" /> Add</Button></div>
              <div className="mt-2 space-y-2">
                {(cfg.faqs || []).map((f, i) => (
                  <div key={f._k || `faq-${i}`} className="rounded-lg border border-slate-200 p-2.5 space-y-2">
                    <Input data-testid={`faq-q-${i}`} value={f.question} onChange={(e) => setFaq(i, "question", e.target.value)} placeholder="Question" className="h-8 text-sm font-medium" />
                    <div className="flex gap-2"><Input data-testid={`faq-a-${i}`} value={f.answer} onChange={(e) => setFaq(i, "answer", e.target.value)} placeholder="Answer" className="h-8 text-sm" /><Button variant="ghost" size="icon" onClick={() => removeFaq(i)} className="h-8 w-8 text-slate-400 hover:text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></Button></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Activate */}
          {!isLive && (
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50/50 p-6 text-center">
              <Sparkles className="h-6 w-6 text-blue-600 mx-auto" />
              <p className="mt-2 font-heading font-bold tracking-tight text-slate-900">Ready when you are</p>
              <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Activating starts your 3-day free trial and takes your AI live to answer real customer calls.</p>
              <Button data-testid="activate-receptionist-btn" onClick={activate} disabled={activating} className="mt-4 h-12 px-8 text-base bg-blue-600 hover:bg-blue-700 gap-2">
                {activating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Rocket className="h-5 w-5" /> Activate AI Receptionist</>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
